import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type {
  AppSurfaceId,
  PendingWearableIntent,
  RetentionMode,
  UserWearableConnection,
  UtilityType,
  WearableExperienceTrack,
  WearableIdentityBaseline,
  WearableSignalType,
} from "@/lib/wearables/wearables.types";
import type {
  WearablesLaunchHandoffPayload,
  WearablesLaunchStage,
} from "../../lib/wearables/wearables.launchHandoff";
import type {
  WearablePathOverrideEntry,
  WearablePathOverrideMemory,
  WearablePathOverrideSurface,
} from "@/lib/wearables/wearables.pathOverrideMemory";
import type {
  WearablesContinuityEntry,
  WearablesContinuityMemory,
  WearablesRouteSurface,
} from "@/lib/wearables/wearables.routeCompanion";

type WearablesState = {
  hydrated: boolean;
  pendingIntent: PendingWearableIntent | null;
  onboardingTrack: WearableExperienceTrack | null;
  identityBaseline: WearableIdentityBaseline | null;
  launchActivation: WearablesLaunchHandoffPayload | null;
  pathAwareOverrides: WearablePathOverrideMemory;
  continuity: WearablesContinuityMemory;
  connections: UserWearableConnection[];
  setHydrated: (hydrated: boolean) => void;
  setPendingIntent: (intent: PendingWearableIntent | null) => void;
  clearPendingIntent: () => void;
  setOnboardingTrack: (track: WearableExperienceTrack | null) => void;
  clearOnboardingTrack: () => void;
  setIdentityBaseline: (baseline: WearableIdentityBaseline | null) => void;
  clearIdentityBaseline: () => void;
  setLaunchActivation: (payload: WearablesLaunchHandoffPayload | null) => void;
  setLaunchActivationStage: (stage: WearablesLaunchStage) => void;
  clearLaunchActivation: () => void;
  setPathAwareOverride: (
    surface: WearablePathOverrideSurface,
    entry: WearablePathOverrideEntry,
  ) => void;
  clearPathAwareOverride: (surface: WearablePathOverrideSurface) => void;
  setContinuityEntry: (
    surface: WearablesRouteSurface,
    entry: WearablesContinuityEntry,
  ) => void;
  clearContinuityEntry: (surface: WearablesRouteSurface) => void;
  upsertConnection: (connection: UserWearableConnection) => void;
  setConnectionStatus: (id: string, status: UserWearableConnection["status"]) => void;
  removeConnection: (id: string) => void;
  setConnectionSignals: (id: string, signals: WearableSignalType[]) => void;
  setConnectionUtilities: (id: string, utilities: UtilityType[]) => void;
  setConnectionSurfaces: (id: string, surfaces: AppSurfaceId[]) => void;
  setConnectionRetentionMode: (id: string, retentionMode: RetentionMode) => void;
  markConnectionGovernanceReviewed: (id: string) => void;
  revokeConnectionConsent: (id: string) => void;
  reset: () => void;
};

type PersistedWearablesState = Partial<
  Pick<
    WearablesState,
    | "pendingIntent"
    | "onboardingTrack"
    | "identityBaseline"
    | "launchActivation"
    | "pathAwareOverrides"
    | "continuity"
    | "connections"
  >
>;

const memoryStorage: Storage = {
  getItem: (key) => (memoryStorage as { _m?: Map<string, string> })._m?.get(key) ?? null,
  setItem: (key, value) => {
    (memoryStorage as { _m?: Map<string, string> })._m ??= new Map();
    (memoryStorage as { _m?: Map<string, string> })._m?.set(key, String(value));
  },
  removeItem: (key) => {
    (memoryStorage as { _m?: Map<string, string> })._m?.delete(key);
  },
  clear: () => {
    (memoryStorage as { _m?: Map<string, string> })._m = new Map();
  },
  key: (index) => {
    const key = Array.from(((memoryStorage as { _m?: Map<string, string> })._m ?? new Map()).keys())[index];
    return key == null ? null : String(key);
  },
  get length() {
    return ((memoryStorage as { _m?: Map<string, string> })._m ?? new Map()).size;
  },
};

const storage = createJSONStorage<Partial<WearablesState>>(() =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined"
    ? window.localStorage
    : memoryStorage,
);

const INITIAL_STATE = {
  hydrated: false,
  pendingIntent: null,
  onboardingTrack: null,
  identityBaseline: null,
  launchActivation: null,
  pathAwareOverrides: {},
  continuity: {},
  connections: [],
};

export const useWearablesStore = create<WearablesState>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,
      setHydrated: (hydrated) => set({ hydrated }),
      setPendingIntent: (pendingIntent) => set({ pendingIntent }),
      clearPendingIntent: () => set({ pendingIntent: null }),
      setOnboardingTrack: (onboardingTrack) => set({ onboardingTrack }),
      clearOnboardingTrack: () => set({ onboardingTrack: null }),
      setIdentityBaseline: (identityBaseline) => set({ identityBaseline }),
      clearIdentityBaseline: () => set({ identityBaseline: null }),
      setLaunchActivation: (launchActivation) => set({ launchActivation }),
      setLaunchActivationStage: (stage) =>
        set((state) => ({
          launchActivation: state.launchActivation
            ? {
                ...state.launchActivation,
                stage,
              }
            : null,
        })),
      clearLaunchActivation: () => set({ launchActivation: null }),
      setPathAwareOverride: (surface, entry) =>
        set((state) => ({
          pathAwareOverrides: {
            ...(state.pathAwareOverrides ?? {}),
            [surface]: entry,
          },
        })),
      clearPathAwareOverride: (surface) =>
        set((state) => {
          const nextOverrides = { ...(state.pathAwareOverrides ?? {}) };
          delete nextOverrides[surface];

          return {
            pathAwareOverrides: nextOverrides,
          };
        }),
      setContinuityEntry: (surface, entry) =>
        set((state) => ({
          continuity: {
            ...(state.continuity ?? {}),
            [surface]: entry,
          },
        })),
      clearContinuityEntry: (surface) =>
        set((state) => {
          const nextContinuity = { ...(state.continuity ?? {}) };
          delete nextContinuity[surface];

          return {
            continuity: nextContinuity,
          };
        }),
      upsertConnection: (connection) =>
        set((state) => {
          const existingIndex = state.connections.findIndex(
            (entry) =>
              entry.id === connection.id ||
              (entry.family === connection.family && entry.brandSlug === connection.brandSlug),
          );
          if (existingIndex === -1) {
            return { connections: [...state.connections, connection] };
          }

          const next = [...state.connections];
          next[existingIndex] = { ...next[existingIndex], ...connection };
          return { connections: next };
        }),
      setConnectionStatus: (id, status) =>
        set((state) => ({
          connections: state.connections.map((connection) =>
            connection.id === id
              ? {
                  ...connection,
                  status,
                  lastSyncedAt: status === "connected" ? new Date().toISOString() : connection.lastSyncedAt,
                }
              : connection,
          ),
        })),
      removeConnection: (id) =>
        set((state) => ({
          connections: state.connections.filter((connection) => connection.id !== id),
        })),
      setConnectionSignals: (id, signals) =>
        set((state) => ({
          connections: state.connections.map((connection) =>
            connection.id === id
              ? {
                  ...connection,
                  signalsEnabled: connection.availableSignals.filter((signal) => signals.includes(signal)),
                }
              : connection,
          ),
        })),
      setConnectionUtilities: (id, utilities) =>
        set((state) => ({
          connections: state.connections.map((connection) =>
            connection.id === id
              ? {
                  ...connection,
                  utilitiesEnabled: connection.availableUtilities.filter((utility) =>
                    utilities.includes(utility),
                  ),
                }
              : connection,
          ),
        })),
      setConnectionSurfaces: (id, surfaces) =>
        set((state) => ({
          connections: state.connections.map((connection) =>
            connection.id === id
              ? {
                  ...connection,
                  surfacesEnabled: connection.availableSurfaces.filter((surface) => surfaces.includes(surface)),
                }
              : connection,
          ),
        })),
      setConnectionRetentionMode: (id, retentionMode) =>
        set((state) => ({
          connections: state.connections.map((connection) =>
            connection.id === id ? { ...connection, retentionMode } : connection,
          ),
        })),
      markConnectionGovernanceReviewed: (id) =>
        set((state) => ({
          connections: state.connections.map((connection) =>
            connection.id === id
              ? {
                  ...connection,
                  consentReviewedAt: new Date().toISOString(),
                  consentRevokedAt: null,
                }
              : connection,
          ),
        })),
      revokeConnectionConsent: (id) =>
        set((state) => ({
          connections: state.connections.map((connection) =>
            connection.id === id
              ? {
                  ...connection,
                  status: "paused",
                  signalsEnabled: [],
                  utilitiesEnabled: [],
                  surfacesEnabled: [],
                  consentRevokedAt: new Date().toISOString(),
                  consentReviewedAt: new Date().toISOString(),
                }
              : connection,
          ),
        })),
      reset: () => set({ ...INITIAL_STATE, hydrated: true }),
    }),
    {
      name: "synktribe-wearables-v1",
      storage,
      partialize: (state) => ({
        pendingIntent: state.pendingIntent,
        onboardingTrack: state.onboardingTrack,
        identityBaseline: state.identityBaseline,
        launchActivation: state.launchActivation,
        pathAwareOverrides: state.pathAwareOverrides,
        continuity: state.continuity,
        connections: state.connections,
      }),
      version: 7,
      migrate: (persistedState, version) => {
        const state = (persistedState ?? {}) as PersistedWearablesState;

        if (version < 3) {
          return {
            pendingIntent: state.pendingIntent ?? null,
            onboardingTrack: null,
            identityBaseline: null,
            pathAwareOverrides: {},
            connections: state.connections ?? [],
          } satisfies PersistedWearablesState;
        }

        if (version < 4) {
          return {
            pendingIntent: state.pendingIntent ?? null,
            onboardingTrack: state.onboardingTrack ?? null,
            identityBaseline: null,
            pathAwareOverrides: {},
            connections: state.connections ?? [],
          } satisfies PersistedWearablesState;
        }

        if (version < 5) {
          return {
            pendingIntent: state.pendingIntent ?? null,
            onboardingTrack: state.onboardingTrack ?? null,
            identityBaseline: state.identityBaseline ?? null,
            launchActivation: null,
            pathAwareOverrides: {},
            connections: state.connections ?? [],
          } satisfies PersistedWearablesState;
        }

        if (version < 6) {
          return {
            pendingIntent: state.pendingIntent ?? null,
            onboardingTrack: state.onboardingTrack ?? null,
            identityBaseline: state.identityBaseline ?? null,
            launchActivation: state.launchActivation ?? null,
            pathAwareOverrides: state.pathAwareOverrides ?? {},
            continuity: {},
            connections: state.connections ?? [],
          } satisfies PersistedWearablesState;
        }

        if (version < 7) {
          return {
            pendingIntent: state.pendingIntent ?? null,
            onboardingTrack: state.onboardingTrack ?? null,
            identityBaseline: state.identityBaseline ?? null,
            launchActivation: state.launchActivation ?? null,
            pathAwareOverrides: state.pathAwareOverrides ?? {},
            continuity: {},
            connections: state.connections ?? [],
          } satisfies PersistedWearablesState;
        }

        return {
          pendingIntent: state.pendingIntent ?? null,
          onboardingTrack: state.onboardingTrack ?? null,
          identityBaseline: state.identityBaseline ?? null,
          launchActivation: state.launchActivation ?? null,
          pathAwareOverrides: state.pathAwareOverrides ?? {},
          continuity: state.continuity ?? {},
          connections: state.connections ?? [],
        } satisfies PersistedWearablesState;
      },
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
    },
  ),
);